import React, { useEffect, useState } from 'react';
import { EmptyContent, Spinner } from '@/components/ui';
import { useNavigate } from 'react-router-dom';

import LogoMark from '@/components/LogoMark';
import { DashboardNoAccess } from '@/components/dashboard-states/DashboardNoAccess';
import { usePermissions } from '@/contexts/PermissionsContext';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { PERMISSIONS } from '@/types/permissions';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/config/routes';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, status } = useAuth();
  const [isMounted, setIsMounted] = useState(false);

  // Always call hooks - they will handle server-side safely now
  const { isLoading: permissionsLoading } = usePermissions();
  const hasBulkFileAccess = usePermissionAccess([PERMISSIONS.FILES_ACCESS]);
  const hasStudyFilesAccess = usePermissionAccess([
    PERMISSIONS.STUDY_FILES_ACCESS,
  ]);
  const hasStudyDataAccess = usePermissionAccess([PERMISSIONS.DATA_ACCESS]);
  const hasQueryImportAccess = usePermissionAccess([
    PERMISSIONS.CSV_UPLOAD,
    PERMISSIONS.CSV_READ_ALL,
  ]);
  const hasAuditTrailAccess = usePermissionAccess([
    PERMISSIONS.AUDIT_TRAIL_ACCESS,
  ]);
  const hasWhitelistAccess = usePermissionAccess([
    PERMISSIONS.WHITELIST_ACCESS,
  ]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (status !== 'success' || !user || !isMounted || permissionsLoading)
      return;

    // Check permissions for each route and redirect to first available
    if (hasStudyFilesAccess) {
      navigate(ROUTES.studyFiles, { replace: true });
      return;
    }

    if (hasBulkFileAccess) {
      navigate(ROUTES.fileImporter, { replace: true });
      return;
    }

    if (hasStudyDataAccess) {
      navigate(ROUTES.studyDataImporter, { replace: true });
      return;
    }

    if (hasQueryImportAccess) {
      navigate(ROUTES.csvImporter, { replace: true });
      return;
    }

    if (hasWhitelistAccess) {
      navigate(ROUTES.whitelistManagement, { replace: true });
      return;
    }

    if (hasAuditTrailAccess) {
      navigate(ROUTES.auditTrail, { replace: true });
      return;
    }

    // If no access to any route, stay on dashboard and show NoAccess
  }, [
    navigate,
    user,
    status,
    isMounted,
    permissionsLoading,
    hasStudyFilesAccess,
    hasBulkFileAccess,
    hasStudyDataAccess,
    hasQueryImportAccess,
    hasWhitelistAccess,
    hasAuditTrailAccess,
  ]);

  // Show loading while checking permissions
  if (status !== 'success' || !user || !isMounted || permissionsLoading) {
    return (
      <EmptyContent
        icon={<LogoMark />}
        title="Loading permissions"
        description="Checking your access rights and determining where to redirect you."
      >
        <Spinner />
      </EmptyContent>
    );
  }

  // If no access to any route, show NoAccess component
  if (
    !hasStudyFilesAccess &&
    !hasBulkFileAccess &&
    !hasStudyDataAccess &&
    !hasQueryImportAccess &&
    !hasWhitelistAccess &&
    !hasAuditTrailAccess
  ) {
    return <DashboardNoAccess />;
  }

  // Show loading while redirecting
  return (
    <EmptyContent
      icon={<LogoMark />}
      title="Check permissions"
      description="Loading permissions and update rules."
    >
      <Spinner />
    </EmptyContent>
  );
};
